/**
 * Comparison Export View Component
 *
 * A specialized view component optimized for PDF/image export.
 * Displays a summary of fund comparison including:
 * - Key metrics comparison
 * - Rank trajectory charts
 * - Key insights
 * - Overlap analysis summary
 *
 * This component is designed to be captured by html2canvas for export.
 */

import { forwardRef } from "react";
import { Star, TrendingUp, TrendingDown, Trophy, AlertTriangle, CheckCircle2 } from "lucide-react";
import type { Fund, FundRating } from "@curie/shared";
import { cn } from "@/lib/utils";
import {
  CATEGORY_LABELS,
  RATING_CONFIG,
  calculateCurieScore,
  formatAum,
  getScoreColor,
} from "./compare-utils";

// ============================================================================
// Types
// ============================================================================

export interface ComparisonExportViewProps {
  /** Funds being compared */
  funds: Fund[];
  /** Whether to include overlap analysis summary */
  includeOverlapSummary?: boolean;
  /** Overlap messages to display */
  overlapMessages?: Array<{
    type: "direct" | "category" | "diversifies";
    message: string;
  }>;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get best fund for a specific metric
 */
function getBestFund(
  funds: Fund[],
  key: keyof Fund | "curieScore",
  lowerIsBetter = false
): Fund | undefined {
  if (funds.length === 0) return undefined;

  return funds.reduce((best, current) => {
    const bestValue = key === "curieScore" ? calculateCurieScore(best) : (best[key] as number);
    const currentValue = key === "curieScore" ? calculateCurieScore(current) : (current[key] as number);

    if (lowerIsBetter) {
      return currentValue < bestValue ? current : best;
    }
    return currentValue > bestValue ? current : best;
  });
}

/**
 * Format percentage with sign
 */
function formatReturn(value: number): string {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%`;
}

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Rating display with stars
 */
function RatingDisplay({ rating }: { rating: FundRating }) {
  const config = RATING_CONFIG[rating];
  return (
    <div className="flex items-center gap-1">
      <div className="flex gap-0.5">
        {Array.from({ length: config.stars }).map((_, i) => (
          <Star key={i} className="h-3 w-3 fill-yellow-400 text-yellow-400" />
        ))}
        {Array.from({ length: 5 - config.stars }).map((_, i) => (
          <Star key={i} className="h-3 w-3 text-gray-300" />
        ))}
      </div>
      <span className={`text-xs font-medium ${config.color}`}>{config.label}</span>
    </div>
  );
}

/**
 * Metric row for comparison table
 */
function MetricRow({
  label,
  funds,
  getValue,
  formatValue,
  bestFundIsin,
  showTrend = false,
}: {
  label: string;
  funds: Fund[];
  getValue: (fund: Fund) => number | string;
  formatValue: (value: number | string) => string;
  bestFundIsin?: string;
  showTrend?: boolean;
}) {
  return (
    <tr className="border-b border-gray-100">
      <td className="py-2 pr-4 text-sm font-medium text-gray-600">{label}</td>
      {funds.map((fund) => {
        const value = getValue(fund);
        const isBest = fund.isin === bestFundIsin;
        const numValue = typeof value === "number" ? value : 0;

        return (
          <td
            key={fund.isin}
            className={cn(
              "py-2 px-3 text-center text-sm",
              isBest && "bg-green-50 font-semibold text-green-700"
            )}
          >
            <div className="flex items-center justify-center gap-1">
              {showTrend && typeof value === "number" && (
                numValue >= 0 ? (
                  <TrendingUp className="h-3 w-3 text-green-600" />
                ) : (
                  <TrendingDown className="h-3 w-3 text-red-600" />
                )
              )}
              <span>{formatValue(value)}</span>
              {isBest && <Trophy className="h-3 w-3 text-green-600 ml-1" />}
            </div>
          </td>
        );
      })}
    </tr>
  );
}

/**
 * Score bar visualization
 */
function ScoreBar({ score, label }: { score: number; label: string }) {
  const colorClass =
    score >= 80 ? "bg-emerald-500" :
    score >= 60 ? "bg-sky-500" :
    score >= 40 ? "bg-amber-500" :
    "bg-rose-500";

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500 w-20">{label}</span>
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`${colorClass} h-full rounded-full transition-all`}
          style={{ width: `${Math.min(100, Math.max(0, score))}%` }}
        />
      </div>
      <span className={`text-xs font-medium w-8 text-right ${getScoreColor(score)}`}>
        {score}
      </span>
    </div>
  );
}

/**
 * Fund card for summary display
 */
function FundSummaryCard({ fund, rank }: { fund: Fund; rank: number }) {
  const curieScore = calculateCurieScore(fund);

  return (
    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-gray-900 truncate">{fund.amcName}</div>
          <div className="text-xs text-gray-500 truncate">{fund.name}</div>
        </div>
        {rank === 1 && (
          <div className="ml-2 flex-shrink-0">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
              <Trophy className="h-3 w-3" />
              Best
            </span>
          </div>
        )}
      </div>

      {/* Rating */}
      <div className="mb-2">
        <RatingDisplay rating={fund.rating} />
      </div>

      {/* Curie Score */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-gray-600">Curie Score</span>
          <span className={`text-lg font-bold ${getScoreColor(curieScore)}`}>
            {curieScore}<span className="text-xs text-gray-400">/100</span>
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={cn(
              "h-full rounded-full",
              curieScore >= 80 ? "bg-emerald-500" :
              curieScore >= 60 ? "bg-sky-500" :
              curieScore >= 40 ? "bg-amber-500" :
              "bg-rose-500"
            )}
            style={{ width: `${curieScore}%` }}
          />
        </div>
      </div>

      {/* Score components */}
      <div className="space-y-1">
        <ScoreBar score={fund.consistencyScore} label="Consistency" />
        <ScoreBar score={fund.recencyScore} label="Recency" />
        <ScoreBar score={fund.volatilityScore} label="Volatility" />
      </div>

      {/* Key metrics */}
      <div className="mt-3 pt-2 border-t border-gray-200 grid grid-cols-2 gap-2 text-xs">
        <div>
          <span className="text-gray-500">Category</span>
          <div className="font-medium">{CATEGORY_LABELS[fund.category] || fund.category}</div>
        </div>
        <div>
          <span className="text-gray-500">Rank</span>
          <div className="font-medium">#{fund.rank} of {fund.totalFundsInCategory}</div>
        </div>
        <div>
          <span className="text-gray-500">Expense</span>
          <div className="font-medium">{fund.expenseRatio.toFixed(2)}%</div>
        </div>
        <div>
          <span className="text-gray-500">AUM</span>
          <div className="font-medium">{formatAum(fund.aum)}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Key insights section
 */
function KeyInsights({ funds }: { funds: Fund[] }) {
  if (funds.length < 2) return null;

  const bestExpense = getBestFund(funds, "expenseRatio", true);
  const bestReturns = getBestFund(funds, "returns5Y");
  const bestScore = getBestFund(funds, "curieScore");

  const insights = [
    bestExpense && {
      icon: <CheckCircle2 className="h-4 w-4 text-green-600" />,
      text: `${bestExpense.amcName} has the lowest expense ratio at ${bestExpense.expenseRatio.toFixed(2)}%`,
    },
    bestReturns && {
      icon: <TrendingUp className="h-4 w-4 text-blue-600" />,
      text: `${bestReturns.amcName} delivered the best 5-year returns at ${formatReturn(bestReturns.returns5Y)}`,
    },
    bestScore && {
      icon: <Trophy className="h-4 w-4 text-amber-600" />,
      text: `${bestScore.amcName} has the highest Curie Score of ${calculateCurieScore(bestScore)}/100`,
    },
  ].filter(Boolean);

  return (
    <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
      <h4 className="text-sm font-semibold text-blue-900 mb-2">Key Insights</h4>
      <ul className="space-y-2">
        {insights.map((insight, index) => (
          <li key={index} className="flex items-start gap-2">
            {insight?.icon}
            <span className="text-xs text-blue-800">{insight?.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Overlap analysis summary
 */
function OverlapSummary({
  messages,
}: {
  messages: Array<{ type: "direct" | "category" | "diversifies"; message: string }>;
}) {
  if (messages.length === 0) return null;

  const iconMap = {
    direct: <AlertTriangle className="h-4 w-4 text-red-600" />,
    category: <AlertTriangle className="h-4 w-4 text-amber-600" />,
    diversifies: <CheckCircle2 className="h-4 w-4 text-green-600" />,
  };

  const bgMap = {
    direct: "bg-red-50 border-red-200",
    category: "bg-amber-50 border-amber-200",
    diversifies: "bg-green-50 border-green-200",
  };

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-gray-700">Portfolio Overlap</h4>
      {messages.map((msg, index) => (
        <div
          key={index}
          className={cn("flex items-start gap-2 p-2 rounded-lg border", bgMap[msg.type])}
        >
          {iconMap[msg.type]}
          <span className="text-xs">{msg.message}</span>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * ComparisonExportView
 *
 * A component optimized for PDF/image export that displays a comprehensive
 * summary of the fund comparison.
 */
export const ComparisonExportView = forwardRef<HTMLDivElement, ComparisonExportViewProps>(
  function ComparisonExportView(
    { funds, includeOverlapSummary = false, overlapMessages = [], className },
    ref
  ) {
    if (funds.length === 0) return null;

    // Sort funds by Curie Score to determine ranking
    const rankedFunds = [...funds].sort(
      (a, b) => calculateCurieScore(b) - calculateCurieScore(a)
    );

    // Get best values for highlighting
    const bestExpense = getBestFund(funds, "expenseRatio", true);
    const bestReturns1Y = getBestFund(funds, "returns1Y");
    const bestReturns3Y = getBestFund(funds, "returns3Y");
    const bestReturns5Y = getBestFund(funds, "returns5Y");
    const bestSharpe = getBestFund(funds, "sharpeRatio");
    const bestAlpha = getBestFund(funds, "alpha");

    return (
      <div
        ref={ref}
        className={cn(
          "bg-white p-6 max-w-4xl mx-auto",
          // Export-optimized styles
          "text-gray-900",
          className
        )}
      >
        {/* Header */}
        <div className="mb-6 pb-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">
            Fund Comparison Summary
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Comparing {funds.length} mutual funds
          </p>
        </div>

        {/* Fund Summary Cards */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Fund Overview</h3>
          <div
            className={cn(
              "grid gap-4",
              funds.length === 2 && "grid-cols-2",
              funds.length === 3 && "grid-cols-3",
              funds.length >= 4 && "grid-cols-2 lg:grid-cols-4"
            )}
          >
            {rankedFunds.map((fund, index) => (
              <FundSummaryCard key={fund.isin} fund={fund} rank={index + 1} />
            ))}
          </div>
        </div>

        {/* Returns Comparison Table */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Performance Comparison</h3>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="py-2 px-3 text-left text-xs font-semibold text-gray-600">
                    Metric
                  </th>
                  {funds.map((fund) => (
                    <th
                      key={fund.isin}
                      className="py-2 px-3 text-center text-xs font-semibold text-gray-600"
                    >
                      {fund.amcName}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <MetricRow
                  label="1-Year Return"
                  funds={funds}
                  getValue={(f) => f.returns1Y}
                  formatValue={(v) => formatReturn(v as number)}
                  bestFundIsin={bestReturns1Y?.isin}
                  showTrend
                />
                <MetricRow
                  label="3-Year Return"
                  funds={funds}
                  getValue={(f) => f.returns3Y}
                  formatValue={(v) => formatReturn(v as number)}
                  bestFundIsin={bestReturns3Y?.isin}
                  showTrend
                />
                <MetricRow
                  label="5-Year Return"
                  funds={funds}
                  getValue={(f) => f.returns5Y}
                  formatValue={(v) => formatReturn(v as number)}
                  bestFundIsin={bestReturns5Y?.isin}
                  showTrend
                />
                <MetricRow
                  label="Expense Ratio"
                  funds={funds}
                  getValue={(f) => f.expenseRatio}
                  formatValue={(v) => `${(v as number).toFixed(2)}%`}
                  bestFundIsin={bestExpense?.isin}
                />
                <MetricRow
                  label="Sharpe Ratio"
                  funds={funds}
                  getValue={(f) => f.sharpeRatio}
                  formatValue={(v) => (v as number).toFixed(2)}
                  bestFundIsin={bestSharpe?.isin}
                />
                <MetricRow
                  label="Alpha"
                  funds={funds}
                  getValue={(f) => f.alpha}
                  formatValue={(v) => {
                    const num = v as number;
                    return `${num >= 0 ? "+" : ""}${num.toFixed(2)}`;
                  }}
                  bestFundIsin={bestAlpha?.isin}
                />
              </tbody>
            </table>
          </div>
        </div>

        {/* Bottom Section: Insights and Overlap */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Key Insights */}
          <KeyInsights funds={funds} />

          {/* Overlap Summary (if enabled) */}
          {includeOverlapSummary && overlapMessages.length > 0 && (
            <OverlapSummary messages={overlapMessages} />
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-200 flex items-center justify-between text-xs text-gray-400">
          <span>Generated by Curie Compass</span>
          <span>{new Date().toLocaleDateString("en-IN", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}</span>
        </div>
      </div>
    );
  }
);

export default ComparisonExportView;
